import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { ErrorCard } from '@/components/ErrorCard'
import { Skeleton } from '@/components/ui/skeleton'
import { RecordBackLink } from '../record/RecordBackLink'
import { CrmApiError } from '../http'
import { getList } from './api'
import { CardScansCard } from './CardScansCard'
import { ListHeader } from './ListHeader'
import { ListMembers } from './ListMembers'
import { MailchimpCard } from './MailchimpCard'
import type { ListDetail } from './types'

/**
 * `/relaties/lijsten/:listId` (spec-63 E5, design §4.7): the head of the list, its members, and in the right
 * column (from 1440 px; below that everything is stacked) the card scans and Mailchimp. Card scans belong to
 * static lists only (hidden on a dynamic one); on a phone the scan card comes first for a list of the kind
 * Event, where the phone is the scanner. `refresh` is the one number every part reads: a change anywhere
 * (members added, a scan finished, the list edited) raises it, and the list and its members are read again.
 */
export function ListPage() {
  const { listId } = useParams<{ listId: string }>()
  const location = useLocation()
  const [list, setList] = useState<ListDetail | null>(null)
  const [error, setError] = useState<'failed' | 'gone' | null>(null)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    if (!listId) return
    let stale = false
    getList(listId).then(
      (found) => {
        if (stale) return
        setList(found)
        setError(null)
      },
      (caught: unknown) => {
        if (!stale) setError(caught instanceof CrmApiError && caught.status === 404 ? 'gone' : 'failed')
      },
    )
    return () => {
      stale = true
    }
  }, [listId, refresh])

  const changed = useCallback(() => setRefresh((count) => count + 1), [])
  const updated = useCallback((next: ListDetail) => {
    setList(next)
    setRefresh((count) => count + 1)
  }, [])

  const from = (location.state as { from?: unknown } | null)?.from
  const back = <RecordBackLink to={`/relaties/lijsten${typeof from === 'string' ? from : ''}`}>Lijsten</RecordBackLink>

  if (error === 'gone') {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <ErrorCard title="Deze lijst bestaat niet meer." description="Ze is verwijderd of de link klopt niet." onRetry={changed} />
      </div>
    )
  }
  if (error === 'failed' && list === null) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <ErrorCard onRetry={changed} />
      </div>
    )
  }
  if (!list) return <Skeleton className="h-10 w-full" />

  const isStatic = list.type === 'static'
  const isEvent = list.kind?.name.trim().toLowerCase() === 'event'
  return (
    <div className="flex flex-col gap-6">
      {back}
      <ListHeader list={list} onChanged={updated} />
      <div className="grid gap-6 min-[1440px]:grid-cols-[minmax(0,1fr)_360px] min-[1440px]:items-start">
        <div className="min-[1440px]:row-span-2">
          <ListMembers list={list} refresh={refresh} onChanged={changed} />
        </div>
        {isStatic ? (
          <div className={isEvent ? 'max-md:order-first' : undefined}>
            <CardScansCard listId={list.id} archived={list.archived_at !== null} onCompleted={changed} />
          </div>
        ) : null}
        <MailchimpCard list={list} onChanged={changed} />
      </div>
    </div>
  )
}
